import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import SideBarModal from '@/components/Modal/SideBarModal';
import { showErrorToast } from '@/components/toast/globalToast';
import {
  cancelSandboxOrder,
  getSandboxOrder,
  SANDBOX_ORDER_NEXT_STATUSES,
  SANDBOX_ORDER_PATH,
  SandboxExchange,
  SandboxOrder,
  SandboxOrderStatus,
  simulateSandboxOrder,
} from '@/services/api/sandbox';
import { extractApiErrorMessage } from '@/utils/errorHandler';
import RequestResponsePanel from './RequestResponsePanel';
import { OrderStatusBadge, orderStatusLabel, totalQty } from './sandboxOrderUi';
import {
  formatDate,
  formatTime,
  RESTING_BUTTON,
  useCooldown,
} from './sandboxUi';

const READ_COOLDOWN_S = 10;
/** The statuses an order can still be cancelled from (before shipping). */
const CANCELLABLE = ['OPEN', 'PREPARING', 'READY_TO_PICKUP'];

const BackLink: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <button
    type="button"
    onClick={onBack}
    className="inline-flex items-center gap-1 text-sm font-medium text-text-sub-600 hover:text-text-strong-950"
  >
    <i className="ri-arrow-left-line" />
    Sandbox orders
  </button>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="min-w-0">
    <dt className="text-xs text-text-sub-600">{label}</dt>
    <dd className="mt-0.5 break-words text-sm text-text-strong-950">
      {children}
    </dd>
  </div>
);

/** The happy path as a track; FAILED / CANCELLED end it with their badge.
 *  The client API has no order timeline, so "reached" is read off the
 *  current status: everything before it on the path. */
const OrderStatusTrack: React.FC<{ status: SandboxOrderStatus }> = ({
  status,
}) => {
  // A cancel's step is not known (the API keeps no order timeline) and the
  // header badge already says Cancelled — no track.
  if (status === 'CANCELLED') return null;
  const offPath = !SANDBOX_ORDER_PATH.includes(status);
  // FAILED happens out for delivery.
  const upTo =
    status === 'FAILED'
      ? SANDBOX_ORDER_PATH.indexOf('IN_DELIVERY')
      : SANDBOX_ORDER_PATH.indexOf(status);
  const steps = offPath
    ? SANDBOX_ORDER_PATH.slice(0, upTo + 1)
    : SANDBOX_ORDER_PATH;
  return (
    <ol
      aria-label="Status track"
      className="flex flex-wrap items-center gap-x-2 gap-y-2 text-xs"
    >
      {steps.map((step, i) => {
        const current = status === step;
        const done = !current && i <= upTo;
        return (
          <li key={step} className="flex items-center gap-2">
            {i > 0 && (
              <span
                aria-hidden="true"
                className={`h-px w-4 ${done || current ? 'bg-primary' : 'bg-stroke-sub-300'}`}
              />
            )}
            <span
              aria-current={current ? 'step' : undefined}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ${
                current
                  ? 'bg-(--primary-alpha-10) text-primary'
                  : done
                    ? 'text-text-strong-950'
                    : 'text-text-soft-400'
              }`}
            >
              {done && <i className="ri-check-line" aria-hidden="true" />}
              {orderStatusLabel(step)}
            </span>
          </li>
        );
      })}
      {offPath && (
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="h-px w-4 bg-primary" />
          <OrderStatusBadge status={status} />
        </li>
      )}
    </ol>
  );
};

/**
 * One sandbox outbound order, read with the same GET a partner makes: what
 * Dash stored (the hub it chose, the lines with their catalog names), the
 * last request this page made, Cancel while it is still cancellable, and the
 * simulator: Next moves it a step (POST …/simulate) as if the warehouse and
 * the courier had done it, until Delivered — or Failed from In delivery.
 */
const SandboxOrderDetail: React.FC<{
  id: string;
  keyPrefix: string | null;
  /** The create that opened this page, shown until the next request. */
  initialExchange: SandboxExchange | null;
  onBack: () => void;
}> = ({ id, keyPrefix, initialExchange, onBack }) => {
  const [order, setOrder] = useState<SandboxOrder | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exchange, setExchange] = useState<SandboxExchange | null>(
    initialExchange
  );
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  /** Which step request is in flight ('next' or the branch status). */
  const [stepping, setStepping] = useState<string | null>(null);
  const readCooldown = useCooldown(READ_COOLDOWN_S);

  // Opening the page and Refresh show that GET in Request & response
  // (`show`); right after a create, the create stays on screen.
  const load = useCallback(
    async ({ show = false } = {}) => {
      try {
        const read = await getSandboxOrder(id);
        if (show) setExchange(read);
        if (read.status >= 400) {
          setLoadError(
            (read.response as { error?: string })?.error ??
              `HTTP ${read.status}`
          );
          return;
        }
        setOrder((read.response as { data: SandboxOrder }).data);
        setLoadError(null);
      } catch (err) {
        setLoadError(extractApiErrorMessage(err, 'Order could not be loaded'));
      }
    },
    [id]
  );

  useEffect(() => {
    load({ show: initialExchange === null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per order
  }, [load]);

  const cancel = async () => {
    setCancelling(true);
    try {
      const result = await cancelSandboxOrder(id, reason.trim());
      setExchange(result);
      if (result.status >= 400) {
        showErrorToast(
          'Cancel refused',
          (result.response as { error?: string })?.error ??
            `HTTP ${result.status}`
        );
      } else {
        setOrder((result.response as { data: SandboxOrder }).data);
      }
      setCancelOpen(false);
    } catch (err) {
      showErrorToast(
        'Cancel failed',
        extractApiErrorMessage(err, 'Could not reach the API')
      );
    } finally {
      setCancelling(false);
    }
  };

  // One simulated step; the response carries the moved order, so no re-read.
  const step = async (key: string, status?: SandboxOrderStatus) => {
    if (stepping) return;
    setStepping(key);
    try {
      const result = await simulateSandboxOrder(id, status);
      setExchange(result);
      if (result.status >= 400) {
        showErrorToast(
          'Step refused',
          (result.response as { error?: string })?.error ??
            `HTTP ${result.status}`
        );
        await load();
      } else {
        setOrder(
          (result.response as { data: { order: SandboxOrder } }).data.order
        );
      }
    } catch (err) {
      showErrorToast(
        'Step failed',
        extractApiErrorMessage(err, 'Could not reach the API')
      );
    } finally {
      setStepping(null);
    }
  };

  if (loadError && !order) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} />
        <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-6 text-sm text-text-sub-600">
          {loadError}
        </div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} />
        <p className="text-sm text-text-soft-400">Loading order…</p>
      </div>
    );
  }

  const canCancel = CANCELLABLE.includes(order.status);
  const resting = readCooldown.left > 0;
  const next = SANDBOX_ORDER_NEXT_STATUSES[order.status] ?? [];
  const busy = stepping !== null || cancelling;

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <BackLink onBack={onBack} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="truncate font-mono text-lg font-semibold text-text-strong-950">
                {order.code}
              </h2>
              <OrderStatusBadge status={order.status} />
            </div>
            <p className="mt-1 text-sm text-text-sub-600">
              <span className="font-mono">{order.externalID}</span> ·{' '}
              {order.hub.name} · created {formatTime(order.createdAt)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="white"
              size="small"
              disabled={resting}
              className={resting ? RESTING_BUTTON : ''}
              onClick={() => {
                readCooldown.start();
                load({ show: true });
              }}
            >
              <i className="ri-refresh-line mr-1.5" />
              {resting ? `Refresh (${readCooldown.left}s)` : 'Refresh'}
            </Button>
          </div>
        </div>
        <OrderStatusTrack status={order.status} />
      </div>

      <div className="flex items-start gap-2.5 rounded-lg bg-bg-weak-50 px-4 py-3 text-xs text-text-sub-600">
        <i className="ri-information-line mt-px text-sm" />
        <span>
          No warehouse picks a sandbox order: press{' '}
          <b className="text-text-strong-950">Next</b> to move it a step as if
          the warehouse and the courier had (Preparing → Ready to pick up →
          Shipped → In delivery → Delivered, or Failed), each with a real{' '}
          <code className="font-mono">POST …/simulate</code>. No stock moves and
          no shipment is created. Order status webhooks are not available yet:
          in production, read the order with GET to follow it until it ships,
          then its shipment sends your shipment webhooks.
        </span>
      </div>

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
        <section className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
          <h3 className="text-base font-semibold text-text-strong-950">
            Order
          </h3>
          <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <Field label="Hub (chosen by Dash)">
              {order.hub.name}
              {order.hub.code && (
                <span className="block font-mono text-xs text-text-sub-600">
                  {order.hub.code}
                </span>
              )}
            </Field>
            <Field label="Recipient">
              {order.recipient.name}
              <span className="block text-xs text-text-sub-600">
                {order.recipient.phone}
              </span>
            </Field>
            <Field label="Expected delivery">
              <span className="tabular-nums">
                {formatDate(order.expectedDeliveryDate)}
              </span>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Destination">
                {order.destination.address}
                {order.destination.city && (
                  <span className="block text-xs text-text-sub-600">
                    {order.destination.city}
                  </span>
                )}
              </Field>
            </div>
            {order.fulfillment?.waybill && (
              <div className="sm:col-span-2">
                <Field label="Shipment (Waybill)">
                  <span className="font-mono">{order.fulfillment.waybill}</span>
                  <span className="block text-xs text-text-sub-600">
                    {[order.fulfillment.carrier, order.fulfillment.status]
                      .filter(Boolean)
                      .join(' · ')}
                    {order.fulfillment.shipmentID
                      ? ''
                      : ' · sandbox — no shipment is created'}
                  </span>
                </Field>
              </div>
            )}
            {order.cancellation && (
              <div className="sm:col-span-2">
                <Field label="Cancelled">
                  {order.cancellation.reason}
                  <span className="block text-xs tabular-nums text-text-sub-600">
                    {formatTime(order.cancellation.cancelledAt)}
                  </span>
                </Field>
              </div>
            )}
          </dl>
        </section>

        <section className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
          <h3 className="text-base font-semibold text-text-strong-950">
            Lines ({order.lines.length})
          </h3>
          <ul className="mt-3 divide-y divide-stroke-soft-200">
            {order.lines.map((l) => (
              <li
                key={l.sku}
                className="flex items-center justify-between gap-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm text-text-strong-950">
                    {l.itemName}
                  </p>
                  <p className="font-mono text-xs text-text-sub-600">{l.sku}</p>
                </div>
                <span className="shrink-0 text-sm tabular-nums text-text-strong-950">
                  {l.qty} {l.uomName}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-right text-xs tabular-nums text-text-sub-600">
            Total qty {totalQty(order.lines)}
          </p>
        </section>
      </div>

      <section className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke-soft-200 px-5 py-3">
          <h3 className="text-sm font-semibold text-text-strong-950">
            Request &amp; response
          </h3>
        </div>
        <div className="p-5">
          <RequestResponsePanel exchange={exchange} keyPrefix={keyPrefix} />
        </div>
      </section>

      {/* ── Action bar (as on sandbox shipments) ─────────────────── */}
      <div
        className={`${
          next.length > 0 || canCancel ? 'sticky bottom-4 z-10' : ''
        } rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-4 py-3 shadow-card-xs sm:px-5`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-1 items-center gap-3 text-sm">
            {canCancel && (
              <Button
                variant="white"
                size="small"
                disabled={busy}
                className="whitespace-nowrap"
                onClick={() => {
                  setReason('');
                  setCancelOpen(true);
                }}
              >
                <i className="ri-close-circle-line mr-1.5 text-error-base" />
                Cancel order
              </Button>
            )}
            <span className="min-w-0 truncate text-text-sub-600">
              {next.length === 0
                ? 'Finished — nothing left to simulate.'
                : `Now: ${orderStatusLabel(order.status)}`}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {next.slice(1).map((status) => (
              <Button
                key={status}
                variant="white"
                disabled={busy}
                onClick={() => step(status, status)}
              >
                {stepping === status ? 'Moving…' : orderStatusLabel(status)}
              </Button>
            ))}
            {next[0] && (
              <Button disabled={busy} onClick={() => step('next')}>
                {stepping === 'next'
                  ? 'Moving…'
                  : `Next: ${orderStatusLabel(next[0])}`}
                <i className="ri-arrow-right-line ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <SideBarModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        width="md"
        className="max-w-[calc(100vw-2rem)]"
        header={
          <div>
            <h2 className="text-lg font-semibold text-text-strong-950">
              Cancel this order?
            </h2>
            <p className="mt-0.5 text-xs text-text-sub-600">
              POST /v1/integration/outbound-orders/:id/cancel
            </p>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="white"
              onClick={() => setCancelOpen(false)}
              disabled={cancelling}
            >
              Back
            </Button>
            <Button variant="danger" onClick={cancel} disabled={cancelling}>
              {cancelling ? 'Cancelling…' : 'Cancel order'}
            </Button>
          </div>
        }
      >
        <div className="space-y-2 p-6">
          <p className="text-sm text-text-sub-600">
            A cancelled order can&apos;t be reopened — create a new one with a
            new external ID.
          </p>
          <label
            htmlFor="sbx-order-reason"
            className="block text-sm font-medium text-text-strong-950"
          >
            Reason{' '}
            <span className="font-normal text-text-sub-600">(optional)</span>
          </label>
          <textarea
            id="sbx-order-reason"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Customer cancelled the order"
            className="w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-strong-950 focus:border-primary focus:outline-none"
          />
        </div>
      </SideBarModal>
    </div>
  );
};

export default SandboxOrderDetail;
